import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import { MdSave } from 'react-icons/md';
import './FiscalCalendarInputPage.scss';
import {
  calPatternsMap,
  CompanyFiscalCal,
  FiscalCalType,
  genPatternBasedCal,
  getMonthName,
  isParseable,
  PatternType
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { DATE_FORMAT } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { SetFiscalCal } from '../../redux/company';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Button } from '../../Components/Button/Button';
import { Design, Intent, Size } from '../../Components/designConstants';
import { Page } from '../../Components/Page';
import { Spinner } from '../../Components/Spinner';
import { ChartContainer } from '../../Components/ChartContainer';
import { QuestionCard } from './QuestionCard';
import { OnChangeHelper, Question, QuestionCardType } from './Question';
import { NestedInputType, NestedOptionConfig, NestedOptionsConfigMap } from './NestedInput';
import { AdminLambdaFetch, ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { useAppDispatch } from '../../redux';
import {
  CAL_TYPES_RADIO_OPTIONS,
  EXTRA_WEEK_TIPS,
  EXTRA_WEEK_YEAR_OPTIONS,
  MONTH_LABEL_CURRENT_QUESTION,
  NESTED_QUESTION_CONFIG,
  OVERWRITE_NOTE,
  PATTERN_QUESTION,
  WEEK_DEFINITION_NOTE,
  WEEK_DEFINITION_OPTIONS,
  YEAR_LABEL_CURRENT_QUESTION
} from './fiscalCalConstants';

export interface FiscalCalFormState extends Omit<CompanyFiscalCal, 'seedDate'> {
  seedDate: string | number | null;
}

const startOfWeekCheck = start => start != null && start >= 0 && start < 7;
const seedDateCheckYearly = date =>
  date != null && typeof date === 'number' ? date > 0 && date < 12 : isDateString(date);
const isDateString = date => date != null && typeof date === 'string' && date.length === 10;
const disableYearCurrentQuestion = (monthCurrent, seedDate) =>
  seedDate != null && monthCurrent === false && Dfns.getMonth(Dfns.parseISO(seedDate)) === 11;
const extraWeekOnChangeHelper = (val: string, state: FiscalCalFormState): FiscalCalFormState => {
  const extraWeekYear = { ...(state.extraWeekYear ?? {}) };
  if (extraWeekYear[val] === undefined) {
    extraWeekYear[val] = 11;
  } else {
    delete extraWeekYear[val];
  }
  return { ...state, extraWeekYear };
};
const allEqualAndNotNil = (obj?: Record<string, number> | null) => {
  if (!obj || Object.values(obj).length === 0) {
    return false;
  }
  const values = new Set(Object.values(obj).filter(val => val != null));
  return values.size === 1;
};
const getWeekDeffNote = (formData: Partial<FiscalCalFormState>) =>
  formData.calType === FiscalCalType.PATTERN ? WEEK_DEFINITION_NOTE : undefined;

const buildFiscalCalFormState = (
  fiscalCal: CompanyFiscalCal | undefined
): Partial<FiscalCalFormState> => {
  if (!fiscalCal?.calType) return {};
  return {
    ...fiscalCal,
    seedDate:
      fiscalCal.calType === FiscalCalType.YEARLY && fiscalCal.seedDate
        ? Dfns.getMonth(Dfns.parseISO(fiscalCal.seedDate))
        : fiscalCal.seedDate
  };
};

export const FiscalCalendarInputPage = () => {
  const { fiscal_cal: fiscalCal, cid } = useCompanyInfo();
  const [saving, setSaving] = useState(false);
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();
  const dispatch = useAppDispatch();
  const [extendExtraWeeks, setExtendExtraWeeks] = useState(
    fiscalCal?.calType === FiscalCalType.PATTERN && !allEqualAndNotNil(fiscalCal.extraWeekYear)
  );
  const [fiscalCalFormData, setFiscalCalFormData] = useState<Partial<FiscalCalFormState>>(() =>
    buildFiscalCalFormState(fiscalCal)
  );

  // If company info wasn't in Redux on first render (API still pending), sync once it arrives.
  const syncedRef = useRef(!!fiscalCal?.calType);
  useEffect(() => {
    if (!syncedRef.current && fiscalCal?.calType) {
      syncedRef.current = true;
      setExtendExtraWeeks(
        fiscalCal.calType === FiscalCalType.PATTERN && !allEqualAndNotNil(fiscalCal.extraWeekYear)
      );
      setFiscalCalFormData(buildFiscalCalFormState(fiscalCal));
    }
  }, [fiscalCal]);

  const updateFiscalCalSettingsState = useCallback((value, path, helper) => {
    setFiscalCalFormData(f => {
      const currCaltype = path === 'calType' && value ? value : f.calType;

      if (currCaltype === FiscalCalType.YEARLY && f.calType !== FiscalCalType.YEARLY) {
        return R.clone({ ...f, calType: currCaltype, seedDate: 1 });
      }
      if (currCaltype === FiscalCalType.PATTERN && f.calType !== FiscalCalType.PATTERN) {
        return R.clone({
          ...f,
          calType: currCaltype,
          pattern: PatternType.PATTERN_A,
          yearLabelCurrent: true,
          monthLabelCurrent: true,
          startOfWeek: null,
          extraWeekYear: {}
        });
      }
      if (
        currCaltype === FiscalCalType.PATTERN &&
        path === 'monthLabelCurrent' &&
        disableYearCurrentQuestion(value, f.seedDate)
      ) {
        // overwrite year month current
        return R.clone({ ...f, yearLabelCurrent: false, [path]: value });
      }
      if (currCaltype === FiscalCalType.PATTERN && path === 'seedDate') {
        const isParseableDate =
          typeof value === 'string' && value.length === 10 && isParseable(value);
        const startOfWeek = isParseableDate ? Dfns.getDay(Dfns.parseISO(value)) : null;
        return R.clone({ ...f, startOfWeek, [path]: value });
      }
      if (helper) {
        return helper(value, R.clone(f));
      }
      return R.clone({ ...f, [path]: value });
    });
  }, []);

  const isUpdate = useMemo(() => fiscalCal != null && fiscalCal.calType != null, [fiscalCal]);
  const updateDiffPresent = useMemo(() => {
    if (isUpdate && fiscalCal != null) {
      for (const key of Object.keys(fiscalCal)) {
        if (
          fiscalCal.calType === FiscalCalType.YEARLY &&
          key === 'seedDate' &&
          typeof fiscalCal[key] === 'string' &&
          typeof fiscalCalFormData[key] === 'number'
        ) {
          if (fiscalCalFormData[key] !== Dfns.getMonth(Dfns.parseISO(fiscalCal[key]))) {
            return true;
          }
        } else if (fiscalCal.calType === FiscalCalType.PATTERN && key === 'startOfWeek') {
          continue;
        } else if (!R.equals(fiscalCal[key], fiscalCalFormData[key])) {
          return true;
        }
      }
    }
    return false;
  }, [fiscalCal, isUpdate, fiscalCalFormData]);

  const formIsValid = useMemo(() => {
    const { calType, pattern, startOfWeek, monthLabelCurrent, yearLabelCurrent, seedDate } =
      fiscalCalFormData;
    if (calType === FiscalCalType.TRADITIONAL) {
      return startOfWeekCheck(startOfWeek);
    }
    if (calType === FiscalCalType.YEARLY) {
      return startOfWeekCheck(startOfWeek) && seedDateCheckYearly(seedDate);
    }
    if (calType === FiscalCalType.PATTERN) {
      // currently not validating 53 week years
      return (
        pattern != null &&
        monthLabelCurrent != null &&
        yearLabelCurrent != null &&
        isDateString(seedDate)
      );
    }
    return false;
  }, [fiscalCalFormData]);

  const saveFiscalCalSettings = useCallback(() => {
    (async () => {
      try {
        setSaving(true);
        const {
          calType,
          startOfWeek,
          extraWeekYear,
          monthLabelCurrent,
          yearLabelCurrent,
          seedDate,
          pattern
        } = fiscalCalFormData;
        const formattedSeedDate =
          calType === FiscalCalType.YEARLY && typeof seedDate === 'number'
            ? Dfns.format(DATE_FORMAT, new Date(2025, seedDate, 1))
            : seedDate;
        const body = {
          company: cid,
          pattern,
          calType,
          yearLabelCurrent,
          monthLabelCurrent,
          seedDate: formattedSeedDate,
          startOfWeek,
          extraWeekYear
        };
        await ClientToolsLambdaFetch('/saveFiscalCalendarInputs', {
          method: 'POST',
          body
        });
        let newSavedInfo;
        switch (calType) {
          case FiscalCalType.TRADITIONAL: {
            newSavedInfo = { calType, startOfWeek };
            break;
          }
          case FiscalCalType.YEARLY: {
            newSavedInfo = { calType, startOfWeek, seedDate: formattedSeedDate };
            break;
          }
          case FiscalCalType.PATTERN: {
            newSavedInfo = {
              calType,
              extraWeekYear,
              monthLabelCurrent,
              yearLabelCurrent,
              seedDate,
              pattern,
              startOfWeek:
                typeof seedDate === 'string' ? Dfns.getDay(Dfns.parseISO(seedDate)) : null
            };
            newSavedInfo.generatedPatternCal = genPatternBasedCal(newSavedInfo);
            break;
          }
          default:
            break;
        }
        dispatch(SetFiscalCal(cid, newSavedInfo));
        try {
          await AdminLambdaFetch('/clearField', {
            method: 'DELETE',
            params: {
              field: cid,
              key: 'companyInfo'
            }
          });
        } catch (e) {
          console.error(`Error clearing company info cache, company: ${cid}`, e.message);
        }
        setError({
          intent: Intent.SUCCESS,
          title: 'Success!',
          message: 'Success saving fiscal calendar settings.'
        });
      } catch (e) {
        setError({
          title: 'Internal Error',
          message: `Error saving fiscal calendar ${e.message}`,
          reportError: e
        });
      } finally {
        setSaving(false);
      }
    })();
  }, [fiscalCalFormData, cid, setError, dispatch]);

  const nestedQuestionConfigMap = useMemo(() => {
    const configToUse = { ...NESTED_QUESTION_CONFIG } as NestedOptionsConfigMap;
    const currVal = fiscalCalFormData.seedDate;
    const isParseableDate = typeof currVal === 'string' && currVal.length === 10;

    if (fiscalCalFormData.calType === FiscalCalType.PATTERN) {
      const patternQuestion = {
        ...PATTERN_QUESTION,
        onChangeHelper: ((value: PatternType, state: FiscalCalFormState): FiscalCalFormState => {
          if (value === state.pattern) {
            return state;
          }
          return { ...state, pattern: value, extraWeekYear: {} };
        }) as OnChangeHelper
      };
      if (isParseableDate) {
        configToUse[FiscalCalType.PATTERN] = [
          ...NESTED_QUESTION_CONFIG[FiscalCalType.PATTERN],
          {
            ...MONTH_LABEL_CURRENT_QUESTION,
            options: Array.from({ length: 12 })
              .map((_, monthIndex) => {
                const monthLabelInd = isParseableDate
                  ? Dfns.getMonth(Dfns.addMonths(monthIndex, Dfns.parseISO(currVal)))
                  : -1;
                const monthName = isParseableDate ? getMonthName(monthLabelInd) : '--';

                let optionValue: boolean | string;
                if (monthIndex === 0) {
                  optionValue = true;
                } else if (monthIndex === 1) {
                  optionValue = false;
                } else {
                  optionValue = `disabledMonth${monthIndex}`;
                }
                return {
                  value: optionValue,
                  label: monthName,
                  disabled: monthIndex > 1,
                  labelInd: monthLabelInd
                };
              })
              .sort((a, b) => a.labelInd - b.labelInd)
          },
          {
            ...YEAR_LABEL_CURRENT_QUESTION,
            options: [
              {
                value: true,
                label: isParseableDate ? `${Dfns.getYear(Dfns.parseISO(currVal))}` : '--'
              },
              {
                value: false,
                label: isParseableDate
                  ? `${Dfns.getYear(Dfns.addYears(1, Dfns.parseISO(currVal)))}`
                  : '--'
              }
            ],
            disable: disableYearCurrentQuestion(
              fiscalCalFormData.monthLabelCurrent,
              fiscalCalFormData.seedDate
            )
          },
          patternQuestion
        ] as NestedOptionConfig[];
      } else {
        configToUse[FiscalCalType.PATTERN] = [
          ...NESTED_QUESTION_CONFIG[FiscalCalType.PATTERN],
          {
            ...MONTH_LABEL_CURRENT_QUESTION,
            options: [],
            disable: true
          },
          {
            ...YEAR_LABEL_CURRENT_QUESTION,
            options: [],
            disable: true
          },
          { ...patternQuestion, disable: true }
        ] as NestedOptionConfig[];
      }
    }

    return configToUse;
  }, [fiscalCalFormData]);

  const patternActiveSeedComplete = useMemo(() => {
    const currVal = fiscalCalFormData.seedDate;
    const isParseableDate = typeof currVal === 'string' && currVal.length === 10;
    const isPattern = fiscalCalFormData.calType === FiscalCalType.PATTERN;
    const lableQuestionsComplete =
      fiscalCalFormData.monthLabelCurrent !== undefined &&
      fiscalCalFormData.yearLabelCurrent !== undefined;
    return isPattern && isParseableDate && lableQuestionsComplete;
  }, [fiscalCalFormData]);

  const seedDateStr =
    typeof fiscalCalFormData.seedDate === 'string' ? fiscalCalFormData.seedDate : null;

  if (!cid) return <Spinner />;

  return (
    <div className='fiscal-calendar-input-page'>
      <Page title='Calendar Inputs' pageType='Calendar Inputs'>
        <ChartContainer
          title='Configure Your Calendar'
          viewRightActions
          rightActions={
            <>
              {isUpdate && updateDiffPresent && (
                <Button
                  size={Size.LG}
                  data-testid='discard-button'
                  design={Design.NEUTRAL}
                  onClick={() => {
                    setFiscalCalFormData(
                      fiscalCal && fiscalCal.calType
                        ? {
                            ...fiscalCal,
                            seedDate:
                              fiscalCal.calType === FiscalCalType.YEARLY && fiscalCal.seedDate
                                ? Dfns.getMonth(Dfns.parseISO(fiscalCal.seedDate))
                                : fiscalCal.seedDate
                          }
                        : {}
                    );
                    setExtendExtraWeeks(
                      fiscalCal &&
                        fiscalCal.calType === FiscalCalType.PATTERN &&
                        !allEqualAndNotNil(fiscalCal.extraWeekYear)
                    );
                  }}
                  disabled={!updateDiffPresent}
                >
                  Discard Changes
                </Button>
              )}
              <Button
                size={Size.LG}
                data-testid='save-button'
                leadingElement={<MdSave />}
                loading={saving}
                onClick={
                  isUpdate
                    ? async () => {
                        try {
                          await setAreYouSure({
                            title: 'Overwrite current calendar settings?',
                            message: OVERWRITE_NOTE,
                            cancelText: 'Cancel',
                            okayText: 'Overwrite',
                            onOkay: () => {
                              saveFiscalCalSettings();
                            }
                          });
                        } catch {
                          // user cancelled the dialog
                        }
                      }
                    : saveFiscalCalSettings
                }
                disabled={saving || !formIsValid || (isUpdate && !updateDiffPresent)}
              >
                Save Calendar
              </Button>
            </>
          }
        >
          <QuestionCard questionNumber='1'>
            <div className='cal-type-question'>
              <Question
                title='What type of calendar do you use?'
                type={QuestionCardType.RADIO}
                subtitle='This determines how your weeks and months are displayed in the app.'
                options={CAL_TYPES_RADIO_OPTIONS}
                nestedOptionsConfig={nestedQuestionConfigMap}
                onChange={updateFiscalCalSettingsState}
                formState={fiscalCalFormData}
                formStateKey='calType'
              />
              {fiscalCalFormData.calType === FiscalCalType.PATTERN && (
                <div className='pattern-preview'>
                  <div className='pattern-preview-label title'>Pattern Preview</div>
                  <div className='pattern-preview-row'>
                    <div className='spacer' />
                    <div className='preview-row-item month-name'>Month Name</div>
                    <div className='preview-row-item num-weeks'># of Weeks</div>
                  </div>
                  {patternActiveSeedComplete && seedDateStr ? (
                    Array.from({ length: 12 }).map((_, monthIndex) => (
                      <div
                        key={`patternPreviewRow${monthIndex}`}
                        className={`pattern-preview-row ${monthIndex % 2 === 0 ? 'stripe-row' : ''}`}
                      >
                        <div className='pattern-preview-label month-count'>{`${monthIndex + 1}.`}</div>
                        <div className='preview-row-item'>
                          {getMonthName(
                            Dfns.getMonth(
                              Dfns.addMonths(
                                monthIndex + (fiscalCalFormData.monthLabelCurrent ? 0 : 1),
                                Dfns.parseISO(seedDateStr)
                              )
                            )
                          )}
                        </div>
                        <div className='preview-row-item'>
                          {
                            calPatternsMap[fiscalCalFormData.pattern || PatternType.PATTERN_A][
                              monthIndex % 3
                            ]
                          }
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='pattern-preview-placeholder'>
                      Complete all of the above questions to see a preview.
                    </div>
                  )}
                  <div className='pattern-preview-label preview-note'>
                    Note: This shows a standard year with 52 weeks, please indicate your 53 week
                    years in question 3.
                  </div>
                </div>
              )}
            </div>
          </QuestionCard>
          <QuestionCard questionNumber='2' hide={fiscalCalFormData.calType == null}>
            <div className='cal-type-question'>
              <Question
                title='How does your company define a week?'
                type={QuestionCardType.RADIO}
                options={WEEK_DEFINITION_OPTIONS}
                onChange={updateFiscalCalSettingsState}
                formState={fiscalCalFormData}
                formStateKey='startOfWeek'
                readOnly={fiscalCalFormData.calType === FiscalCalType.PATTERN}
                note={getWeekDeffNote(fiscalCalFormData)}
              />
            </div>
          </QuestionCard>
          <QuestionCard
            questionNumber='3'
            hide={fiscalCalFormData.calType !== FiscalCalType.PATTERN}
          >
            <div className='additional-pattern-questions'>
              <div>
                <Question
                  title='How do you handle 53-week years?'
                  type={QuestionCardType.RADIO}
                  readOnly={!patternActiveSeedComplete}
                  options={[
                    { label: 'Same month always gets the extra week', value: false },
                    {
                      label: 'Different months get the extra week in different years',
                      value: true
                    }
                  ]}
                  onChange={value => {
                    setExtendExtraWeeks(value);
                    if (!value) {
                      setFiscalCalFormData(f => ({
                        ...f,
                        extraWeekYear: {}
                      }));
                    }
                  }}
                  formState={extendExtraWeeks}
                  formStateKey={null}
                  nestedOptionsConfig={{
                    false: [
                      {
                        inputType: NestedInputType.DROPDOWN_MULTI,
                        label: 'All past and future fiscal years with a 53rd week:',
                        dropdownProps: {
                          itemOnClick: year => {
                            updateFiscalCalSettingsState(year, '', extraWeekOnChangeHelper);
                          }
                        },
                        options: EXTRA_WEEK_YEAR_OPTIONS.map(year => ({
                          ...year,
                          selected:
                            fiscalCalFormData &&
                            fiscalCalFormData.extraWeekYear &&
                            fiscalCalFormData.extraWeekYear[`${year.value}`] !== undefined
                        })),
                        formState: fiscalCalFormData,
                        formSetter: updateFiscalCalSettingsState,
                        formPath: 'extraWeekYear',
                        onChangeHelper: extraWeekOnChangeHelper as OnChangeHelper,
                        checkOptions: () =>
                          fiscalCalFormData && fiscalCalFormData.extraWeekYear
                            ? Object.keys(fiscalCalFormData.extraWeekYear).join(', ')
                            : ''
                      },
                      {
                        inputType: NestedInputType.DROPDOWN,
                        label: 'Fiscal month with an extra week:',
                        disable:
                          !patternActiveSeedComplete ||
                          !fiscalCalFormData ||
                          !fiscalCalFormData.extraWeekYear ||
                          Object.keys(fiscalCalFormData.extraWeekYear).length === 0,
                        options:
                          patternActiveSeedComplete && seedDateStr
                            ? new Array(12).fill(null).map((el, i) => ({
                                label: `${i + 1}. ${getMonthName(
                                  Dfns.getMonth(
                                    Dfns.addMonths(
                                      i + (fiscalCalFormData.monthLabelCurrent ? 0 : 1),
                                      Dfns.parseISO(seedDateStr)
                                    )
                                  )
                                )}`,
                                value: `${i}`
                              }))
                            : [],
                        formState: fiscalCalFormData,
                        formPath: 'extraWeekYear',
                        formSetter: updateFiscalCalSettingsState,
                        onChangeHelper: ((
                          val: number,
                          state: FiscalCalFormState
                        ): FiscalCalFormState => {
                          const extraWeekYear = Object.fromEntries(
                            Object.keys(state.extraWeekYear ?? {}).map(year => [year, Number(val)])
                          );
                          return { ...state, extraWeekYear };
                        }) as OnChangeHelper,
                        checkOptions: () => {
                          if (!fiscalCalFormData || !patternActiveSeedComplete) return '';
                          if (allEqualAndNotNil(fiscalCalFormData.extraWeekYear)) {
                            return `${Object.values(fiscalCalFormData.extraWeekYear as Record<string, number>)[0]}`;
                          }
                          return '11';
                        }
                      }
                    ],
                    true: [
                      {
                        inputType: NestedInputType.CUSTOM,
                        formPath: 'extraWeekYear',
                        formState: fiscalCalFormData,
                        customContent: (
                          <div className='extra-weeks-questions'>
                            <div className='extra-weeks-labels'>
                              <span>Year:</span>
                              <span>Extra week in:</span>
                            </div>
                            <Question
                              type={QuestionCardType.CHECKBOX}
                              options={EXTRA_WEEK_YEAR_OPTIONS}
                              onChange={updateFiscalCalSettingsState}
                              formState={fiscalCalFormData}
                              formStateKey='extraWeekYear'
                              alwasyShowNestedQuestion
                              nestedOptionsConfig={
                                patternActiveSeedComplete && seedDateStr
                                  ? EXTRA_WEEK_YEAR_OPTIONS.reduce<NestedOptionsConfigMap>(
                                      (acc, cv) => {
                                        if (
                                          fiscalCalFormData &&
                                          fiscalCalFormData.extraWeekYear &&
                                          fiscalCalFormData.extraWeekYear[`${cv.value}`] !==
                                            undefined
                                        ) {
                                          acc[cv.label] = [
                                            {
                                              inputType: 'dropdown',
                                              options: new Array(12).fill(null).map((el, i) => ({
                                                label: `${i + 1}. ${getMonthName(
                                                  Dfns.getMonth(
                                                    Dfns.addMonths(
                                                      i +
                                                        (fiscalCalFormData.monthLabelCurrent
                                                          ? 0
                                                          : 1),
                                                      Dfns.parseISO(seedDateStr)
                                                    )
                                                  )
                                                )}`,
                                                value: `${i}`
                                              })),
                                              formPath: 'extraWeekYear',
                                              dropdownProps: {
                                                size: Size.SM
                                              },
                                              onChangeHelper: ((
                                                val: number,
                                                state: FiscalCalFormState
                                              ): FiscalCalFormState => ({
                                                ...state,
                                                extraWeekYear: {
                                                  ...(state.extraWeekYear ?? {}),
                                                  [cv.label]: Number(val)
                                                }
                                              })) as OnChangeHelper,
                                              checkOptions: () => {
                                                if (
                                                  fiscalCalFormData &&
                                                  fiscalCalFormData.extraWeekYear &&
                                                  fiscalCalFormData.extraWeekYear[cv.label] !==
                                                    undefined
                                                ) {
                                                  return `${fiscalCalFormData.extraWeekYear[cv.label]}`;
                                                }
                                                return '11';
                                              }
                                            }
                                          ];
                                        }
                                        return acc;
                                      },
                                      {}
                                    )
                                  : undefined
                              }
                              checkOption={value =>
                                Boolean(
                                  fiscalCalFormData &&
                                  fiscalCalFormData.extraWeekYear &&
                                  fiscalCalFormData.extraWeekYear[value] !== undefined
                                )
                              }
                              onChangeHelper={extraWeekOnChangeHelper as OnChangeHelper}
                            />
                          </div>
                        )
                      }
                    ]
                  }}
                />
                <div className='nested-input-note'>
                  Important:
                  <ul>
                    <li key='extraWeekTipImportant'>
                      Include years before your current fiscal year start date for reporting
                      accuracy.
                    </li>
                  </ul>
                  Tip:
                  <ul>
                    {EXTRA_WEEK_TIPS.map((tip, i) => (
                      <li key={`extraWeekTip${i}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </QuestionCard>
        </ChartContainer>
      </Page>
    </div>
  );
};
